import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, Skeleton, Card, Alert } from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Bar } from "react-chartjs-2";
import axios from "axios";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { authState, popupState } from "../../Recoil/RecoilState";
import { chartColors, baseURL } from "../../utils/StaticVariables";
import DesBtn from "../Reusable/DesBtn";
import OrderFilter from "../Charts/OrderFilter";

const commonOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top", labels: { color: "gray" } },
    title: { display: false },
  },
  scales: {
    x: { ticks: { color: "gray" } },
    y: { ticks: { color: "gray" }, beginAtZero: true },
  },
};

const CameraIncidentsChart = ({
  featureTitle = "Incidents",
  apiEndpoint = "",
}) => {
  const [dataList, setDataList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useRecoilValue(authState);
  const setPopup = useSetRecoilState(popupState);
  const childRef = useRef(null);
  const [filter, setFilter] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { ...filter };
        if (params.locations) { params.location = params.locations; delete params.locations; }
        if (params.buildings) { params.building = params.buildings; delete params.buildings; }
        if (params.zones) { params.zone = params.zones; delete params.zones; }

        if (apiEndpoint) {
          const { data } = await axios.get(apiEndpoint, {
            headers: { Authorization: `Bearer ${token}` },
            params,
          });
          setDataList(data);
        } else {
          // Fallback camera events summary
          const { data } = await axios.get(`${baseURL}shoplifting/dashboard/executive-summary`, {
            headers: { Authorization: `Bearer ${token}` },
            params,
          });
          setDataList(data);
        }
      } catch (err) {
        console.error("Error fetching camera incidents chart data:", err);
        setError("Unable to load incident statistics.");
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchData();
  }, [filter, token, apiEndpoint]);

  const openFilter = () => {
    setPopup({
      isOpen: true,
      title: "Select Date, Time and Location Filters",
      content: (
        <OrderFilter ref={childRef} filter={filter} changeFilterHandle={setFilter} />
      ),
      sendReq: () => childRef.current?.submit(),
    });
  };

  // A bare list is the shared evidence-analytics shape (lost-found/blocked-exit
  // incidents-per-camera): [{camera_name, events, incidents, resolved}].
  const incidentsByCamera =
    (Array.isArray(dataList) ? dataList : null) ||
    dataList?.incidents_by_camera ||
    dataList?.incidents_by_location ||
    dataList?.data ||
    [];

  const labels = incidentsByCamera.map(
    (item) => item.camera_name || item.location || item.name || "Unknown"
  );
  const counts = incidentsByCamera.map(
    (item) => item.incident_count || item.incidents || item.count || item.total || 0
  );

  return (
    <Card sx={{ p: 2, height: "auto", bgcolor: "background.paper", mb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Number of Incidents per Camera — {featureTitle}
        </Typography>
        <DesBtn text="Filter" handle={openFilter}>
          <FilterAltOutlinedIcon />
        </DesBtn>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rectangular" height="400px" sx={{ borderRadius: 2 }} />
      ) : (
        <Paper elevation={0} sx={{ p: 2, height: 420, bgcolor: "background.paper" }}>
          <Box sx={{ height: 360 }}>
            <Bar
              data={{
                labels: labels.length ? labels : ["No Data Available"],
                datasets: [
                  {
                    label: "Incident Count",
                    data: counts.length ? counts : [0],
                    backgroundColor: chartColors,
                    borderRadius: 4,
                  },
                ],
              }}
              options={commonOptions}
            />
          </Box>
        </Paper>
      )}
    </Card>
  );
};

export default CameraIncidentsChart;
